/**
 * Bulk Actions V2 Composable
 *
 * Composable for bulk actions functionality for CollectionItem resources.
 * Supports both bookmarks and notes through the unified CollectionItem abstraction.
 * Uses excludeItemIds instead of excludeBookmarkIds.
 *
 * @returns {Object} Bulk actions state and methods
 */
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import CollectionMember from '@/store/models/collections/CollectionMember'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import Collection from '@/store/models/collections/Collection'
import errorHandler from '@/plugins/error-handler'
import toast from '@/plugins/toasts'
import safeAreaInsets from '@/plugins/safe-area-insets'
import useConfirmModal from './useConfirmModal'
import { useRestoreDeletedItems } from './useRestoreDeletedItems'

export function useBulkActionsV2() {
  const store = useStore()
  const router = useRouter()
  const { t } = useI18n()

  // State
  const isCollectionInviteDialogOpen = ref(false)
  const isBulkCollectionSelectDialogOpen = ref(false)
  const loadingBulk = ref({
    tags: false,
  })

  // ==================== COMPUTED PROPERTIES (GETTERS) ====================

  const bulkNumberOfItems = computed(() => store.getters['bulkActionsV2/numberOfItems'])
  const numberOfCollections = computed(() => store.getters['bulkActionsV2/numberOfCollections'])
  const isBulkActionsActive = computed(() => store.getters['bulkActionsV2/isActive'])
  const showBulkActions = computed(() => store.getters['bulkActionsV2/showActions'])
  const bulkSelectedItems = computed(() => store.getters['bulkActionsV2/items'])
  const selectedCollections = computed(() => store.getters['bulkActionsV2/collections'])
  const selectedOwnedCollections = computed(() => store.getters['bulkActionsV2/ownedCollections'])
  const selectedNotOwnedCollections = computed(() => store.getters['bulkActionsV2/notOwnedCollections'])
  const numberOfSelectedItems = computed(() => store.getters['bulkActionsV2/numberOfSelectedItems'])
  const bulkIsAllSelected = computed(() => store.getters['bulkActionsV2/isAllSelected'])
  const numberOfDeletedItems = computed(() => store.getters['collectionItems/deletedItemCount'])
  const numberOfItems = computed(() => store.getters['collectionItems/itemCount'])
  const collectionId = computed(() => store.getters['collectionItems/collectionId'])
  const bulkTagFilters = computed(() => store.getters['bulkActionsV2/tagFilters'])
  const bulkTagsNext = computed(() => bulkTagFilters.value.next)
  const userId = computed(() => store.state.auth.userId)
  const isCheckboxChecked = computed(() => store.getters['bulkActionsV2/isCheckboxChecked'])

  // ==================== MUTATIONS ====================

  const toggleBulkActions = () => store.commit('bulkActionsV2/toggleActions')
  const closeBulkActions = () => store.commit('bulkActionsV2/closeActions')
  const bulkActionClearSelected = () => store.commit('bulkActionsV2/clear')
  const setIsCollectionInviteDialogOpen = value => store.commit('bulkActionsV2/setIsCollectionInviteDialogOpen', value)
  const bulkSetIsAllSelected = value => store.commit('bulkActionsV2/setIsAllSelected', value)
  const bulkSetTagsNext = value => store.commit('bulkActionsV2/setTagsNext', value)
  const setPublicSourceContext = value => store.commit('bulkActionsV2/setPublicSourceContext', value)

  const { openModal } = useConfirmModal()
  const { restoreItems: restoreDeletedItems } = useRestoreDeletedItems()

  // ==================== METHODS ====================

  /**
   * Load tags for selected items
   */
  async function loadTagsBulk(ownerId) {
    try {
      loadingBulk.value.tags = true
      await store.dispatch('bulkActionsV2/getTags', ownerId)
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      loadingBulk.value.tags = false
    }
  }

  /**
   * Toggle select all items
   */
  async function bulkToggleAllSelected() {
    store.commit('bulkActionsV2/toggleAllSelected')
  }

  /**
   * Add selected items to favorites
   */
  async function addBulkToFavorites() {
    try {
      const count = bulkNumberOfItems.value
      await store.dispatch('bulkActionsV2/toggleFavorite', {
        isFavorite: true,
      })
      toast.success(t('bulk.toast.added_to_favorites', count))
      return count
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Remove selected items from favorites
   */
  async function removeBulkFromFavorites() {
    try {
      const count = bulkNumberOfItems.value
      await store.dispatch('bulkActionsV2/toggleFavorite', {
        isFavorite: false,
      })
      toast.success(t('bulk.toast.removed_from_favorites', count))
      return count
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Soft delete selected items
   */
  async function deleteBulkSelected() {
    try {
      const count = bulkNumberOfItems.value

      const confirmDialog = await openModal('confirm', {
        props: {
          returnValue: 'Something',
        },
        payload: {
          title: t('bulk.dialog.delete.title', count),
          text: t('bulk.dialog.delete.text', count),
          confirmBtnText: t('common.delete'),
        },
      })

      const confirmed = await confirmDialog.result
      if (!confirmed) return

      await store.dispatch('bulkActionsV2/delete')
      await store.dispatch('collectionItems/getStats')
      toast.success(t('bulk.toast.deleted', count))
      return count
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Permanently delete selected items
   */
  async function deleteFullBulkSelected() {
    try {
      const count = bulkNumberOfItems.value

      const confirmDialog = await openModal('confirm', {
        payload: {
          title: t('bulk.dialog.delete_permanently.title', count),
          text: t('bulk.dialog.delete_permanently.text', count),
          confirmBtnText: t('common.delete'),
        },
      })

      const confirmed = await confirmDialog.result
      if (!confirmed) return

      await store.dispatch('bulkActionsV2/fullDelete')
      await store.dispatch('collectionItems/getStats')
      toast.success(t('bulk.toast.deleted_permanently', count))
      return count
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Restore deleted selected items
   */
  async function restoreBulkSelected() {
    try {
      // Unified trash: items can span many (alive or deleted) collections. Classify +
      // confirm-new-collection + restore is centralized in useRestoreDeletedItems.
      const result = await restoreDeletedItems(store.getters['bulkActionsV2/items'])
      if (!result) return // cancelled in the confirm dialog
      await store.dispatch('collectionItems/getStats')
      return result.restored?.length ?? 0
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Save selected items to user's private collection.
   * Supports both private collections (standard API) and public collections
   * (public copy API) via publicSourceContext in the store.
   */
  async function addBulkToMyItems(ignoreExists = false) {
    const count = bulkNumberOfItems.value
    const publicContext = store.state.bulkActionsV2.publicSourceContext

    async function dispatchSave(opts = {}) {
      if (publicContext) {
        const privateCollectionId = store.getters['auth/collectionId']
        await store.dispatch('collections/copyPublicCollectionItemsBulk', {
          ...publicContext,
          collectionIds: [privateCollectionId],
          isPrivate: true,
          isAllSelected: store.state.bulkActionsV2.isAllSelected,
          excludeItemIds: store.state.bulkActionsV2.selection.excludeItemIds,
          ...opts,
        })
      } else {
        await store.dispatch('bulkActionsV2/save', opts)
      }
    }

    try {
      await dispatchSave({ ignoreExists })
      if (!publicContext) await store.dispatch('collectionItems/getStats')
      toast.success(t('bulk.toast.saved', count))
      return { success: true, numberOfItems: count }
    } catch (error) {
      if (error.message === 'BOOKMARK_EXISTS' && !ignoreExists) {
        const confirmDialog = await openModal('confirm', {
          payload: {
            title: t('bulk.dialog.duplicate_bookmark.title'),
            text: t('bulk.dialog.duplicate_bookmark.collection.text'),
            confirmBtnText: t('cta.add_anyway'),
          },
        })
        const confirmed = await confirmDialog.result
        if (!confirmed) return { cancelled: true }

        await dispatchSave({ ignoreExists: true })
        if (!publicContext) await store.dispatch('collectionItems/getStats')
        toast.success(t('bulk.toast.saved', count))
        return { success: true, numberOfItems: count }
      }
      errorHandler.toast(error)
      return { error: true }
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Navigate to add items to collections page
   */
  function addBulkToCollections(targetCollectionId = '') {
    if (targetCollectionId) {
      router.push({
        path: `/collections/${targetCollectionId}/bulk/add-collections`,
        query: router.currentRoute.value.query,
      })
    } else {
      router.push({
        path: '/bookmarks/bulk/add-collections',
        query: router.currentRoute.value.query,
      })
    }
  }

  /**
   * Navigate to bulk add tags page
   */
  function bulkAddTags(targetCollectionId = '') {
    if (targetCollectionId) {
      router.push({
        path: `/collections/${targetCollectionId}/bulk/add-tags`,
        query: router.currentRoute.value.query,
      })
    } else {
      router.push({
        path: '/bookmarks/bulk/add-tags',
        query: router.currentRoute.value.query,
      })
    }
  }

  /**
   * Navigate to bulk remove tags page
   */
  function bulkRemoveTags(targetCollectionId = '') {
    if (targetCollectionId) {
      router.push({
        path: `/collections/${targetCollectionId}/bulk/remove-tags`,
        query: router.currentRoute.value.query,
      })
    } else {
      router.push({
        path: '/bookmarks/bulk/remove-tags',
        query: router.currentRoute.value.query,
      })
    }
  }

  /**
   * Toggle selection state for an item
   */
  function bulkToggleItemSelection(item) {
    const isCurrentlySelected = isItemSelected(item.id)

    if (isCurrentlySelected) {
      store.commit('bulkActionsV2/removeItem', item.id)
    } else {
      store.commit('bulkActionsV2/addItem', item.id)
    }
  }

  /**
   * Check if an item is selected
   */
  function isItemSelected(itemId) {
    const state = store.state.bulkActionsV2
    if (state.isAllSelected) {
      return !state.selection.excludeItemIds.includes(itemId)
    }
    return state.selection.excludeItemIds.includes(itemId)
  }

  /**
   * Toggle selection state for a collection
   */
  function toggleCollectionBulk(collection) {
    const isCurrentlySelected = isCollectionSelected(collection.id)

    if (isCurrentlySelected) {
      store.commit('bulkActionsV2/removeCollection', collection.id)
    } else {
      store.commit('bulkActionsV2/addCollection', collection.id)
    }
  }

  /**
   * Check if a collection is selected
   */
  function isCollectionSelected(targetCollectionId) {
    const state = store.state.bulkActionsV2
    if (state.isAllSelected) {
      return !state.selection.excludeCollectionIds.includes(targetCollectionId)
    }
    return state.selection.excludeCollectionIds.includes(targetCollectionId)
  }

  /**
   * Delete or leave selected collections
   */
  async function bulkDeleteOrLeaveCollections() {
    try {
      const notOwnedCollectionIds = selectedNotOwnedCollections.value.map(i => i.id)

      const members = CollectionMember.query()
        .where('userId', userId.value)
        .where(collectionMember => notOwnedCollectionIds.includes(collectionMember.collectionId))
        .get()

      const memberIds = members.map(i => i.id)

      const confirmDialog = await openModal('confirm', {
        payload: {
          title: t('bulk.dialog.delete_or_leave_collection.title', selectedCollections.value.length),
          text: t('bulk.dialog.delete_or_leave_collection.text', selectedCollections.value.length),
          confirmBtnText: t('common.okay'),
        },
      })

      const confirmed = await confirmDialog.result
      if (!confirmed) return

      const jobs = []

      if (selectedOwnedCollections.value.length > 0) {
        jobs.push(store.dispatch('bulkActionsV2/deleteCollections', {
          collections: selectedOwnedCollections.value,
        }))
      }

      if (selectedNotOwnedCollections.value.length > 0) {
        jobs.push(store.dispatch('bulkActionsV2/removeMember', {
          collections: selectedNotOwnedCollections.value,
          memberIds,
        }))
      }

      await Promise.all(jobs)
      return { success: true, count: selectedCollections.value.length }
    } catch (error) {
      errorHandler.toast(error)
      return { error: true }
    } finally {
      toggleBulkActions()
    }
  }

  /**
   * Open collection invite dialog
   */
  function bulkInviteContributors() {
    isCollectionInviteDialogOpen.value = true
  }

  /**
   * Navigate to bulk edit share link page
   */
  function bulkEditInviteLink() {
    router.push('/collections/bulk/edit-share-link')
  }

  return {
    // State
    isCollectionInviteDialogOpen,
    isBulkCollectionSelectDialogOpen,
    loadingBulk,

    // Computed properties
    bulkNumberOfItems,
    numberOfCollections,
    isBulkActionsActive,
    showBulkActions,
    bulkSelectedItems,
    selectedCollections,
    selectedOwnedCollections,
    selectedNotOwnedCollections,
    numberOfSelectedItems,
    bulkIsAllSelected,
    numberOfDeletedItems,
    numberOfItems,
    collectionId,
    bulkTagFilters,
    bulkTagsNext,
    userId,
    isCheckboxChecked,

    // Mutations
    toggleBulkActions,
    closeBulkActions,
    bulkActionClearSelected,
    setIsCollectionInviteDialogOpen,
    bulkSetIsAllSelected,
    bulkSetTagsNext,
    setPublicSourceContext,

    // Methods
    loadTagsBulk,
    bulkToggleAllSelected,
    addBulkToFavorites,
    removeBulkFromFavorites,
    deleteBulkSelected,
    deleteFullBulkSelected,
    restoreBulkSelected,
    addBulkToMyItems,
    addBulkToCollections,
    bulkAddTags,
    bulkRemoveTags,
    bulkToggleItemSelection,
    toggleCollectionBulk,
    bulkDeleteOrLeaveCollections,
    bulkInviteContributors,
    bulkEditInviteLink,

    // Selection helpers
    isItemSelected,
    isCollectionSelected,

    // Extra utilities
    errorHandler,
    toast,
    safeAreaInsets,
  }
}
